import { Box, Chip } from "@mui/material";
import { FORM_STATUS_ENUM } from "@interfaces/response";
import { capitalizeFirstLetter } from "@helpers/funciones";

/** Los valores del estado, sin el `null` que también admite `FORM_STATUS_ENUM`. */
type EstadoConEstilo = NonNullable<FORM_STATUS_ENUM>;

interface EstiloEstado {
  fondo: string;
  borde: string;
  texto: string;
  punto: string;
}

/**
 * Un color por estado. Salen de las variables `--ld-*` del rediseño (`styles.css`), así que la
 * familia es la misma en los cuatro: fondo muy claro, borde un tono más firme, texto oscuro
 * legible sobre el fondo y el punto en el color pleno.
 */
const ESTILOS: Record<EstadoConEstilo, EstiloEstado> = {
  PENDIENTE: { fondo: "#F2F5F9", borde: "#E4E7EC", texto: "#4A5567", punto: "#7A8497" },
  COMPLETO: { fondo: "#FFF8EB", borde: "#FCE4B0", texto: "#B45309", punto: "#D97706" },
  VALIDADO: { fondo: "#ECFDF3", borde: "#C9F0D6", texto: "#15803D", punto: "#16A34A" },
  INCIDENCIA: { fondo: "#FEF3F2", borde: "#FBD3D0", texto: "#B42318", punto: "#D92D20" },
};

interface Props {
  /** El mismo `FORM_STATUS_ENUM` que traen los formularios del expediente. */
  formStatus: FORM_STATUS_ENUM;
}

/**
 * Estado de un elemento del expediente: la pastilla `.bdg` del diseño — punto de color, texto
 * capitalizado, fondo claro y borde del mismo tono.
 *
 * Un estado `null` se pinta como `PENDIENTE`, igual que hace `ButtonExpedienteStatus` con el mismo
 * tipo en el expediente legacy.
 *
 * No confundir con `Badge` del kit, que es la pastilla de fondo transparente y borde de color que
 * usa `UploadCard` para el estado de un documento.
 */
export const ChipEstadoExpediente = ({ formStatus }: Props) => {
  const estadoVisible = formStatus ?? "PENDIENTE";
  const estilo = ESTILOS[estadoVisible];
  // En minúsculas primero: el helper solo sube la inicial y el enum viene todo en mayúsculas.
  const etiqueta = capitalizeFirstLetter(estadoVisible.toLowerCase());

  return (
    <Chip
      label={etiqueta}
      icon={<Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: estilo.punto, flexShrink: 0 }} />}
      sx={{
        height: "auto",
        p: "3px 10px",
        gap: "5px",
        borderRadius: "999px",
        bgcolor: estilo.fondo,
        border: `1px solid ${estilo.borde}`,
        color: estilo.texto,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.01em",
        // El `Chip` de MUI trae su propio padding y margen; los pone el contenedor de arriba.
        "& .MuiChip-label": { p: 0 },
        "& .MuiChip-icon": { m: 0 },
      }}
    />
  );
};
